/**
 * This is the list of polyfills detected by running the following command:
 * `npx create-polyfill-service-url analyse --file ./build/js/fwn.js --file ./build/js/videoplayer-{hash}.js`
 *
 * The list is then manually sorted and cleaned up. `null` means that the polyfill
 * is no longer available (see the comment bellow) or is an alias to some other polyfill.
 *
 * =================================================================================================
 * Bellow is the list of polyfills removed from polyfill.io as the
 * support for IE8 was removed. See https://github.com/Financial-Times/polyfill-library/pull/1153
 *
 * Array.isArray
 * Array.prototype.every
 * Array.prototype.filter
 * Array.prototype.forEach
 * Array.prototype.indexOf
 * Array.prototype.lastIndexOf
 * Array.prototype.map
 * Array.prototype.reduce
 * Array.prototype.reduceRight
 * Array.prototype.some
 * Date.now
 * Date.prototype.toISOString
 * document
 * document.getElementsByClassName
 * document.head
 * document.querySelector
 * Element
 * Element.prototype.cloneNode
 * Element.prototype.nextElementSibling
 * Element.prototype.previousElementSibling
 * Function.prototype.bind
 * getComputedStyle
 * navigator.geolocation
 * Object.create
 * Object.defineProperties
 * Object.defineProperty
 * Object.getPrototypeOf
 * String.prototype.anchor
 * String.prototype.big
 * String.prototype.blink
 * String.prototype.bold
 * String.prototype.fixed
 * String.prototype.fontcolor
 * String.prototype.fontsize
 * String.prototype.italics
 * String.prototype.link
 * String.prototype.small
 * String.prototype.strike
 * String.prototype.sub
 * String.prototype.sup
 * XMLHttpRequest
 * ~viewport
 *
 * This is the list of polyfills removed from polyfill.io as the support
 * for <IE8 was removed. See https://github.com/Financial-Times/polyfill-library/pull/1149
 *
 * JSON
 * localStorage
 * Window
 */

// Import all the detect modules
import AbortControllerDetect from './detects/polyfill-library/polyfills/AbortController/detect'
import ArrayFromDetect from './detects/polyfill-library/polyfills/Array/from/detect'
import ArrayEntriesDetect from './detects/polyfill-library/polyfills/Array/prototype/entries/detect'
import ArrayFillDetect from './detects/polyfill-library/polyfills/Array/prototype/fill/detect'
import ArrayFindDetect from './detects/polyfill-library/polyfills/Array/prototype/find/detect'
import ArrayFindIndexDetect from './detects/polyfill-library/polyfills/Array/prototype/findIndex/detect'
import ArrayFlatDetect from './detects/polyfill-library/polyfills/Array/prototype/flat/detect'
import ArrayIncludesDetect from './detects/polyfill-library/polyfills/Array/prototype/includes/detect'
import ArrayKeysDetect from './detects/polyfill-library/polyfills/Array/prototype/keys/detect'
import ArraySortDetect from './detects/polyfill-library/polyfills/Array/prototype/sort/detect'
import ArrayValuesDetect from './detects/polyfill-library/polyfills/Array/prototype/values/detect'
import ArrayBufferDetect from './detects/polyfill-library/polyfills/ArrayBuffer/detect'
import BlobDetect from './detects/polyfill-library/polyfills/Blob/detect'
import ConsoleDetect from './detects/polyfill-library/polyfills/console/detect'
import CustomEventDetect from './detects/polyfill-library/polyfills/CustomEvent/detect'
import DocumentCurrentScriptDetect from './detects/polyfill-library/polyfills/document/currentScript/detect'
import DocumentVisibilityStateDetect from './detects/polyfill-library/polyfills/document/visibilityState/detect'
import EventDetect from './detects/polyfill-library/polyfills/Event/detect'
import FetchDetect from './detects/polyfill-library/polyfills/fetch/detect'
import IntersectionObserverDetect from './detects/polyfill-library/polyfills/IntersectionObserver/detect'
import MapDetect from './detects/polyfill-library/polyfills/Map/detect'
import MutationObserverDetect from './detects/polyfill-library/polyfills/MutationObserver/detect'
import NumberIsNaNDetect from './detects/polyfill-library/polyfills/Number/isNaN/detect'
import NumberMaxSafeIntegerDetect from './detects/polyfill-library/polyfills/Number/MAX_SAFE_INTEGER/detect'
import NumberMinSafeIntegerDetect from './detects/polyfill-library/polyfills/Number/MIN_SAFE_INTEGER/detect'
import ObjectAssignDetect from './detects/polyfill-library/polyfills/Object/assign/detect'
import ObjectFreezeDetect from './detects/polyfill-library/polyfills/Object/freeze/detect'
import ObjectGetOwnPropertyDescriptorDetect from './detects/polyfill-library/polyfills/Object/getOwnPropertyDescriptor/detect'
import ObjectGetOwnPropertyDescriptorsDetect from './detects/polyfill-library/polyfills/Object/getOwnPropertyDescriptors/detect'
import ObjectGetOwnPropertyNamesDetect from './detects/polyfill-library/polyfills/Object/getOwnPropertyNames/detect'
import ObjectHasOwnDetect from './detects/polyfill-library/polyfills/Object/hasOwn/detect'
import ObjectIsFrozenDetect from './detects/polyfill-library/polyfills/Object/isFrozen/detect'
import ObjectSealDetect from './detects/polyfill-library/polyfills/Object/seal/detect'
import ObjectSetPrototypeOfDetect from './detects/polyfill-library/polyfills/Object/setPrototypeOf/detect'
import PromiseDetect from './detects/polyfill-library/polyfills/Promise/detect'
import ReflectConstructDetect from './detects/polyfill-library/polyfills/Reflect/construct/detect'
import ReflectDetect from './detects/polyfill-library/polyfills/Reflect/detect'
import RequestAnimationFrameDetect from './detects/polyfill-library/polyfills/requestAnimationFrame/detect'
import ResizeObserverDetect from './detects/polyfill-library/polyfills/ResizeObserver/detect'
import SetDetect from './detects/polyfill-library/polyfills/Set/detect'
import StringIncludesDetect from './detects/polyfill-library/polyfills/String/prototype/includes/detect'
import StringPadEndDetect from './detects/polyfill-library/polyfills/String/prototype/padEnd/detect'
import StringStartsWithDetect from './detects/polyfill-library/polyfills/String/prototype/startsWith/detect'
import StringTrimDetect from './detects/polyfill-library/polyfills/String/prototype/trim/detect'
import SymbolDetect from './detects/polyfill-library/polyfills/Symbol/detect'
import SymbolIteratorDetect from './detects/polyfill-library/polyfills/Symbol/iterator/detect'
import SymbolPrototypeDescriptionDetect from './detects/polyfill-library/polyfills/Symbol/prototype/description/detect'
import SymbolToStringTagDetect from './detects/polyfill-library/polyfills/Symbol/toStringTag/detect'
import URLDetect from './detects/polyfill-library/polyfills/URL/detect'
import WeakMapDetect from './detects/polyfill-library/polyfills/WeakMap/detect'
import WeakSetDetect from './detects/polyfill-library/polyfills/WeakSet/detect'

/**
 * Keeping `null` values for features which are still reported by
 * the `create-polyfill-service-url` tool but no longer supported by polyfill.io
 *
 * Ignored by prettier for easier maintenance of one-liners.
 */
// prettier-ignore
export const features = [
  ['AbortController', AbortControllerDetect()],
  ['Array.from', ArrayFromDetect()],
  ['Array.isArray', null],
  ['Array.prototype.entries', ArrayEntriesDetect()],
  ['Array.prototype.every', null],
  ['Array.prototype.fill', ArrayFillDetect()],
  ['Array.prototype.filter', null],
  ['Array.prototype.find', ArrayFindDetect()],
  ['Array.prototype.findIndex', ArrayFindIndexDetect()],
  ['Array.prototype.flat', ArrayFlatDetect()],
  ['Array.prototype.forEach', null],
  ['Array.prototype.includes', ArrayIncludesDetect()],
  ['Array.prototype.indexOf', null],
  ['Array.prototype.keys', ArrayKeysDetect()],
  ['Array.prototype.lastIndexOf', null],
  ['Array.prototype.map', null],
  ['Array.prototype.reduce', null],
  ['Array.prototype.some', null],
  ['Array.prototype.sort', ArraySortDetect()],
  ['Array.prototype.values', ArrayValuesDetect()],
  ['ArrayBuffer', ArrayBufferDetect()],
  ['Blob', BlobDetect()],
  ['console', ConsoleDetect()],
  ['CustomEvent', CustomEventDetect()],
  ['DataView', ArrayBufferDetect()], // Alias for ArrayBuffer
  ['Date.now', null],
  ['Date.prototype.toISOString', null],
  ['document', null],
  ['document.currentScript', DocumentCurrentScriptDetect()],
  ['document.visibilityState', DocumentVisibilityStateDetect()], // new
  ['Element', null],
  ['Event', EventDetect()],
  ['fetch', FetchDetect()],
  ['Float32Array', null], // Alias for `ArrayBuffer`
  ['Function.prototype.bind', null],
  ['getComputedStyle', null],
  ['IntersectionObserver', IntersectionObserverDetect()],
  // TODO(ZE-336): The polyfill.io code raises an error. We need to investigate why.
  // ['Intl.DateTimeFormat', IntlDateTimeFormatDetect],
  // ['Intl.NumberFormat', IntlNumberFormatDetect],
  ['JSON', null],
  ['localStorage', null],
  ['Map', MapDetect()],
  ['modernizr:es5object', null], // Alias to `Object.keys`, `Object/getOwnPropertyNames` and `Object/getOwnPropertyDescriptor`
  ['MutationObserver', MutationObserverDetect()],
  ['Number.isNaN', NumberIsNaNDetect()],
  ['Number.MAX_SAFE_INTEGER', NumberMaxSafeIntegerDetect()],
  ['Number.MIN_SAFE_INTEGER', NumberMinSafeIntegerDetect()],
  ['Object.assign', ObjectAssignDetect()],
  ['Object.defineProperties', null],
  ['Object.defineProperty', null],
  ['Object.freeze', ObjectFreezeDetect()],
  ['Object.getOwnPropertyDescriptor', ObjectGetOwnPropertyDescriptorDetect()],
  ['Object.getOwnPropertyDescriptors', ObjectGetOwnPropertyDescriptorsDetect()],
  ['Object.getOwnPropertyNames', ObjectGetOwnPropertyNamesDetect()],
  ['Object.getOwnPropertySymbols', null], // Alias for `Symbol`
  ['Object.hasOwn', ObjectHasOwnDetect()],
  ['Object.isFrozen', ObjectIsFrozenDetect()],
  ['Object.seal', ObjectSealDetect()],
  ['Object.setPrototypeOf', ObjectSetPrototypeOfDetect()],
  ['Promise', PromiseDetect()],
  ['Reflect', ReflectDetect()],
  ['Reflect.construct', ReflectConstructDetect()],
  ['requestAnimationFrame', RequestAnimationFrameDetect()],
  ['ResizeObserver', ResizeObserverDetect()],
  ['Set', SetDetect()],
  ['String.prototype.includes', StringIncludesDetect()],
  ['String.prototype.padEnd', StringPadEndDetect()],
  ['String.prototype.startsWith', StringStartsWithDetect()],
  ['String.prototype.trim', StringTrimDetect()],
  ['Symbol', SymbolDetect()],
  ['Symbol.for', null], // Alias for `Symbol`
  ['Symbol.iterator', SymbolIteratorDetect()],
  ['Symbol.prototype.description', SymbolPrototypeDescriptionDetect()],
  ['Symbol.toStringTag', SymbolToStringTagDetect()],
  ['Uint16Array', null], // Alias for `ArrayBuffer`
  ['Uint8Array', null], // Alias for `ArrayBuffer`
  ['URL', URLDetect()],
  ['URLSearchParams', null], // Alias for `URL`
  ['WeakMap', WeakMapDetect()],
  ['WeakSet', WeakSetDetect()],
  ['XMLHttpRequest', null],
]
